'use client'

import SectionLayout from '@component/layout/section'
import Select from '@component/select'
import useFetch from '@hooks/useFetch'
import { toPrice } from '@utils'
import classNames from 'classnames'
import { assign, each, isFunction, last, map, mapValues } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Charts from './charts'

export default function Content({
  propertyType,
  bedrooms,
  years,
  dataType,
  suburbId,
  suburbName
}) {

  const [filter, setFilter] = useState({
    propertyType: getDefaultValue(propertyType),
    bedrooms: getDefaultValue(bedrooms),
    years: getDefaultValue(years),
    dataType: getDefaultValue(dataType),
  })
  function filterChangeHandle(key) {
    return val => {
      setFilter(d => ({ ...d, [key]: val }))
    }
  }
  function getDefaultValue(list) {
    let res = list[0]
    each(list, item => {
      if (item.defaultSelect) res = item
    })
    return res
  }

  // 每个dataType的数据
  const [chartsData, setChartsData] = useState({})
  // 每个dataType是否loading
  const [chartsLoading, setChartsLoading] = useState({})
  // 当前选中的dataType数据
  const [currentData, currentLoading] = useMemo(() => {
    if (!filter.dataType) return [null, false]
    return [
      chartsData[filter.dataType.value] || null,
      chartsLoading[filter.dataType.value] || false
    ]
  }, [chartsData, chartsLoading, filter.dataType])
  const chartsLoadHandle = useCallback((item, data) => {
    setChartsData(d => ({ ...d, [item.value]: data }))
  }, [setChartsData])
  const chartsLoadingHandle = useCallback((item, v) => {
    setChartsLoading(d => ({ ...d, [item.value]: v }))
  }, [setChartsLoading])

  // 每个dataType的参数
  const params = useMemo(() => {
    return mapValues(filter, item => item ? item.value : '')
  }, [filter])

  const subTitle = useMemo(() => {
    let bedroomLabel = filter.bedrooms.value === 5 ? 'all bedroom' : filter.bedrooms.label
    return `Insights for ${bedroomLabel} homes in ${suburbName}`
  }, [filter.bedrooms, suburbName])

  return (
    <SectionLayout title='Market insights' sub={subTitle}>
      <div className='flex flex-col flex-wrap relative z-[1] -mt-20 md:items-center md:justify-between md:flex-row md:space-x-10'>
        <div className='flex items-end space-x-8 mt-20 flex-1 md:flex-none max-w-full'>
          <FilterItem title='Property type' className='md:w-220' popWidth={250} data={propertyType} value={filter.propertyType} onChange={filterChangeHandle('propertyType')}></FilterItem>
          <FilterItem title='Bedrooms' className='md:w-118' popWidth={140} data={bedrooms} value={filter.bedrooms} onChange={filterChangeHandle('bedrooms')}></FilterItem>
          <FilterItem title='Time (yrs)' className='md:w-80' popWidth={90} data={years} value={filter.years} onChange={filterChangeHandle('years')}></FilterItem>
        </div>
        <div className='flex space-x-4 h-90 mt-10 flex-1 md:flex-none max-w-full overflow-auto'>{
          map(dataType, item => (
            <TabItem key={item.label} data={item} active={filter.dataType && filter.dataType.value === item.value} id={suburbId} params={params} onChange={filterChangeHandle('dataType')} onLoad={chartsLoadHandle} onLoading={chartsLoadingHandle}></TabItem>
          ))
        }</div>
      </div>
      <div className='-mt-1'>
        <Charts data={currentData} dataType={filter.dataType} isLoading={currentLoading} yearLabel={filter.years.label}></Charts>
      </div>
    </SectionLayout>
  )
}

function FilterItem({
  title,
  data,
  value,
  onChange,
  className,
  popWidth
}) {

  return (
    <div className='flex-1'>
      <div className='mb-4 text-font text-sm'>{title}</div>
      <Select data={data} value={value} onChange={onChange} border popWidth={popWidth} labelClassName={classNames('h-40 text-sm w-full', className)}></Select>
    </div>
  )
}

function TabItem({
  data,
  active = false,
  onChange,
  id,
  params,
  onLoad,
  onLoading
}) {

  const { data: marketData, isLoading } = useFetch({ url: `suburb/${id}/value-rents`, params: assign({}, params, { dataType: data.value }) })
  const hasData = marketData && marketData.data.length > 0
  const valueLabel = hasData ? labelFormat(last(marketData.data).label) : ''

  useEffect(() => {
    if (!isLoading && isFunction(onLoad)) {
      onLoad(data, marketData)
    }
  }, [hasData, marketData, isLoading, data, onLoad])
  useEffect(() => {
    if (isFunction(onLoading)) onLoading(data, isLoading)
  }, [data, isLoading, onLoading])

  function clickHandle() {
    onChange(data)
  }
  function labelFormat(label) {
    let res = label
    if (`${label}`.indexOf('$') !== -1 || !isNaN(label)) {
      res = toPrice(`${label}`)
    }
    return res
  }

  return (
    <div className={classNames(
      'bg-neutral-1 cursor-pointer border border-b-0 rounded-[4px_4px_0_0] w-160 flex-col items-center sm:justify-evenly text-font flex-1 py-4',
      { '!text-primary !bg-white': active },
      hasData ? 'flex' : 'hidden'
      // hasData ? 'flex' : 'flex'
    )} onClick={clickHandle}>
      <div className='text-xs sm:text-sm text-center flex-1 min-h-0 sm:flex-none'>{data.label}</div>
      <div className='text-lg sm:text-3xl'>{valueLabel}</div>
    </div>
  )
}
